import {adminRoutes} from '@app/admin/admin-routes';
import {dashboardRoutes} from '@app/dashboard/dashboard-routes';
import {helpCenterRoutes} from '@app/help-center/help-center-routes';
import {servicesRoutes} from '@app/services/services-routes';
import {directoryRoutes} from '@app/directory/directory-routes';
import {opportunitiesRoutes} from '@app/opportunities/opp-routes';
import {appearanceRoutes} from '@app/appearance/appearance-routes';
import {staffRoutes} from '@app/staff/staff-routes';
import {studentRoutes} from '@app/student/student-routes';
import {companyRoutes} from '@app/company/company-routes';
import {authRoutes} from '@common/auth/auth-routes';
import {authGuard} from '@common/auth/guards/auth-route';
import {ImpersonationBar} from '@app/staff/impersonation-bar';
import {RootErrorElement, RootRoute} from '@common/core/common-provider';
import {commonRoutes} from '@common/core/common-routes';
import {notificationRoutes} from '@common/notifications/notification-routes';
import {HcLivechatWidgetLoader} from '@livechat/hc-livechat-widget-loader';
import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';
import {FullPageLoader} from '@ui/progress/full-page-loader';
import {createBrowserRouter, useRouteError} from 'react-router';
import {isStaleChunkError} from '@app/stale-chunk-recovery';
import {AppUpdatedMessage} from '@app/app-updated-message';
import {Fragment} from 'react/jsx-runtime';
import {CartDrawer} from '@app/checkout/cart-drawer';
import {AppearanceStyle} from '@app/appearance/theme-style';
import {AppearanceAdminBar} from '@app/appearance/admin-bar/appearance-admin-bar';
import {SiteHeaderBlocks} from '@app/appearance/block-area';
import {ProfileCompletionGate} from '@app/auth/profile-completion-gate';
import {useReferralCapture} from '@app/student/partner/use-referral-capture';
import {usePageViewTracking} from '@app/student/use-page-view-tracking';
import {useLauncherLift} from '@app/use-launcher-lift';
import {ReferralWelcomeDialog} from '@app/referrals/referral-offer';
import {StaffAgreementGate} from '@app/staff/agreement/staff-agreement-gate';

function RootAppRoute() {
  // Mounted at the root because a partner link can point at ANY page — a
  // scholarship, the homepage, a course. Reading `?ref=` only on the sign-up
  // page would attribute almost nothing.
  useReferralCapture();

  // Which pages a signed-in person opened — the activity timeline on their
  // customer record. Mounted at the root for the same reason as the line above:
  // it has to see every page, not one.
  usePageViewTracking();

  // Keep the floating chat button clear of a page's own bottom bar.
  useLauncherLift();

  return (
    <Fragment>
      {/* Above everything, because an impersonated session can be on any page
          and "how do I get back to being me" must not depend on which one. */}
      <ImpersonationBar />
      <SiteHeaderBlocks />
      <RootRoute />
      <ProfileCompletionGate />
      <StaffAgreementGate />
      <HcLivechatWidgetLoader />
      <CartDrawer />
      <ReferralWelcomeDialog />
      <AppearanceStyle />
      <AppearanceAdminBar />
    </Fragment>
  );
}

/**
 * A page whose code chunk no longer exists (the site was updated while this tab
 * was open) gets a calm "updating" screen that reloads once. Every other error
 * goes to the normal error page unchanged.
 */
function AppRootErrorElement() {
  const error = useRouteError();
  return isStaleChunkError(error) ? <AppUpdatedMessage /> : <RootErrorElement />;
}

export const appRouter = createBrowserRouter(
  [
    {
      id: 'root',
      element: <RootAppRoute />,
      errorElement: <AppRootErrorElement />,
      hydrateFallbackElement: <FullPageLoader screen />,
      children: [
        {
          path: '/',
          lazy: () => import('@app/help-center/homepage/hc-landing-page'),
        },
        {
          path: 'livechat',
          lazy: () => import('@livechat/chat-page/chat-page'),
        },
        {
          path: 'livechat/:conversationId',
          lazy: () => import('@livechat/chat-page/chat-page'),
        },
        ...authRoutes({
          loginRoute: {
            lazy: () => import('@app/auth/app-login-page'),
          },
          registerRoute: {
            // Step 3 "register in both": creates the account in the CRM
            // (lead + application custom fields + login contact) and mirrors it
            // into Flash, then logs in. Agent-invite signup still uses the
            // Common register page (see /agents/join route below).
            lazy: () => import('@app/auth/crm-register-page'),
          },
          accountSettingsRoute: {
            lazy: () => import('@app/auth/app-account-settings-page'),
          },
        }),
        {
          path: 'forum',
          lazy: () => import('@app/opportunities/forum-page'),
        },
        {
          path: 'cv-builder',
          lazy: () => import('@app/student/cv-builder/cv-builder-public-page'),
        },
        {
          path: 'cv-builder/editor',
          lazy: () => import('@app/student/cv-builder/cv-editor-page'),
        },
        ...notificationRoutes,
        ...helpCenterRoutes,
        ...servicesRoutes,
        ...directoryRoutes,
        ...opportunitiesRoutes,
        ...appearanceRoutes,
        ...dashboardRoutes,
        ...adminRoutes,
        ...staffRoutes,
        ...studentRoutes,
        ...companyRoutes,
        // Public "why join" pages in front of the company and partner
        // sign-ups — a visitor sees what they get before being asked to sign in.
        {
          path: 'for-companies',
          lazy: () => import('@app/programmes/company-landing-page'),
        },
        {
          path: 'partner',
          lazy: () => import('@app/programmes/partner-landing-page'),
        },
        {
          // Public employer/institution signup (no auth — creates the account).
          path: 'company/register',
          lazy: () => import('@app/company/company-register-page'),
        },
        {
          // Team invite landing (public preview; accept requires login).
          path: 'company/join/:token',
          lazy: () => import('@app/company/company-join-page'),
        },
        {
          // Public company profile — linked from job cards, offers and the
          // portal's "View public profile".
          path: 'companies/:slug',
          lazy: () => import('@app/company/public/company-public-profile-page'),
        },
        {
          // Client-facing offer link. Public by design — the unguessable token
          // is the credential, and the API only serves shared, unexpired offers.
          path: 'offer/:token',
          lazy: () => import('@app/company/offers/shared-offer-page'),
        },
        ...commonRoutes,
        {
          path: '/agents/join/:inviteId',
          handle: {inviteType: 'agentInvite'},
          lazy: () => import('@app/auth/app-register-page'),
        },
        {
          path: 'api-docs',
          loader: () =>
            authGuard({permission: 'api.access', requireLogin: false}),
          lazy: () => import('@common/swagger/swagger-api-docs-page'),
        },
      ],
    },
  ],
  {
    basename: getBootstrapData().settings.html_base_uri,
  },
);
